import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  BookOpen,
  Headphones,
  Sparkles,
  CheckCircle2,
  Gift,
  Loader2,
  Lock,
  Heart,
  Star,
  ArrowRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUNDLE_ITEMS = [
  {
    icon: BookOpen,
    label: 'The Complete E-Book',
    value: '$19.99',
    detail: 'Taking Your Power Back — the full Magnetic Mind Method with worksheets and recode scripts. Downloadable PDF.',
    color: 'from-maroon to-primary',
    tint: 'primary',
  },
  {
    icon: Headphones,
    label: 'Power Planning Practice App',
    value: '$9.99',
    detail: '13 guided meditations, printable worksheets, and a daily practice you can carry in your pocket.',
    color: 'from-secondary to-teal-600',
    tint: 'secondary',
  },
  {
    icon: Sparkles,
    label: 'The Interactive E-Course',
    value: '$29',
    detail: 'Read the entire course online — 5 chapters, journaling prompts, progress tracking, and dark mode. No login required.',
    color: 'from-primary to-secondary',
    tint: 'primary',
  },
];

const TESTIMONIAL_LINES = [
  {
    quote: 'I read the book in a weekend and I have not stopped talking about the emotional set point work. Life-changing.',
    author: 'Sarah M.',
    role: 'Verified reader',
  },
  {
    quote: "The morning practice took me from reactive to intentional in under two weeks. I don't know who I was before this.",
    author: 'Andrea K.',
    role: 'Practice App user',
  },
  {
    quote: "Breah's voice on the meditations feels like a friend guiding you home. I do them every single morning.",
    author: 'Jenn R.',
    role: 'Daily practitioner',
  },
];

export default function BundleLandingPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const claim = async (e) => {
    e?.preventDefault?.();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const referrer = params.get('src') || params.get('utm_source') || document.referrer || null;
      const { data } = await axios.post(`${API}/bundle/claim`, {
        name: name.trim() || null,
        email: email.trim().toLowerCase(),
        origin_url: window.location.origin,
        referrer,
      });
      // Stash for welcome page (survives refresh, cleared after visit)
      sessionStorage.setItem('bundle-claim', JSON.stringify({
        name: name.trim() || null,
        email: email.trim().toLowerCase(),
        ebook_download_url: data.ebook_download_url,
        practice_portal_url: data.practice_portal_url,
      }));
      navigate('/bundle/welcome');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="bundle-landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-maroon/10 via-background to-secondary/10" />
        <div className="absolute top-20 -left-20 w-80 h-80 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-10 -right-10 w-96 h-96 rounded-full bg-secondary/10 blur-3xl" />

        <div className="relative container mx-auto px-4 pt-14 pb-10 md:pt-20 md:pb-16 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="bg-primary/15 text-primary border-primary/30 uppercase tracking-widest text-[11px] font-bold px-3 py-1 mb-5" data-testid="bundle-badge">
              <Gift className="w-3 h-3 mr-1.5" /> Free Bundle · $58.98 Value
            </Badge>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-[1.05] mb-5" data-testid="bundle-headline">
              The Taking Your Power Back <span className="italic text-primary">Bundle</span>
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-4 leading-relaxed">
              The complete E-Book, the Power Planning Practice App, <em>and</em> the online E-Course &mdash; <strong className="text-foreground">yours free</strong>. Everything I use with private clients, in one place.
            </p>

            <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground mb-8">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-secondary" /> Instant access</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-secondary" /> No credit card</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-secondary" /> Yours to keep</span>
            </div>
          </motion.div>

          {/* CAPTURE FORM */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            <Card className="max-w-xl mx-auto p-6 sm:p-8 bg-card/90 backdrop-blur-sm border-2 border-primary/25 shadow-xl" data-testid="bundle-form-card">
              <form onSubmit={claim} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5" htmlFor="bundle-name">
                    Your first name <span className="text-muted-foreground/60 font-normal normal-case tracking-normal">(optional)</span>
                  </label>
                  <Input
                    id="bundle-name"
                    type="text"
                    placeholder="Sarah"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-12 text-base"
                    data-testid="bundle-name-input"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5" htmlFor="bundle-email">
                    Where should I send your bundle?
                  </label>
                  <Input
                    id="bundle-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 text-base"
                    data-testid="bundle-email-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-14 text-base font-bold bg-gradient-to-r from-primary to-maroon text-primary-foreground hover:opacity-90 shadow-lg"
                  data-testid="bundle-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending your bundle...
                    </>
                  ) : (
                    <>
                      Send me the free bundle <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-[11px] text-center text-muted-foreground flex items-center justify-center gap-1.5">
                  <Lock className="w-3 h-3" /> Your email is safe. Unsubscribe anytime. No spam, ever.
                </p>
              </form>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="container mx-auto px-4 py-14 max-w-5xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-secondary mb-2">What&rsquo;s Inside</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-3">Three Gifts. One Practice.</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Together, these are the exact tools I use with my private clients. Delivered instantly to your inbox.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {BUNDLE_ITEMS.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
              >
                <Card className="p-6 h-full border-silver/25 hover:border-primary/40 hover:shadow-lg transition-all" data-testid={`bundle-item-${i}`}>
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shrink-0 shadow-md`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Gift {i + 1}</p>
                      <h3 className="font-display text-xl font-bold text-foreground leading-tight">{item.label}</h3>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground line-through">{item.value}</p>
                      <p className="text-xs font-bold text-secondary uppercase">Free</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.detail}</p>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Total value strip */}
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <span className="text-sm text-muted-foreground">Total value:</span>
          <span className="text-2xl font-display font-bold text-foreground line-through opacity-60">$58.98</span>
          <ArrowRight className="w-5 h-5 text-primary" />
          <span className="text-3xl font-display font-extrabold text-primary">Yours Free</span>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-gradient-to-b from-transparent via-primary/5 to-transparent py-14">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-secondary mb-2">What people are saying</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">Real transformation. Real people.</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIAL_LINES.map((t, i) => (
              <motion.div
                key={t.author}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <Card className="p-6 h-full border-silver/25 bg-card" data-testid={`bundle-testimonial-${i}`}>
                  <div className="flex text-secondary mb-3">
                    {[...Array(5)].map((_, s) => <Star key={s} className="w-4 h-4 fill-current" />)}
                  </div>
                  <p className="text-sm text-foreground leading-relaxed mb-4 italic">&ldquo;{t.quote}&rdquo;</p>
                  <div>
                    <p className="text-sm font-bold text-foreground">{t.author}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="container mx-auto px-4 py-16 max-w-3xl text-center">
        <Heart className="w-10 h-10 text-primary mx-auto mb-4" strokeWidth={1.5} />
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
          Ready to take your power back?
        </h2>
        <p className="text-muted-foreground mb-8 text-lg">
          Everything you need is one email away. No catch, no timer, no upsell wall.
        </p>
        <Button
          size="lg"
          onClick={() => document.getElementById('bundle-email')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
          className="h-14 px-8 text-base font-bold bg-gradient-to-r from-primary to-maroon text-primary-foreground shadow-lg"
          data-testid="bundle-scroll-to-form"
        >
          <Sparkles className="w-4 h-4 mr-2" /> Claim my free bundle
        </Button>
        <p className="text-xs text-muted-foreground mt-6">
          Delivered instantly to your inbox &middot; Log in with a magic link &middot; Yours forever
        </p>
      </section>
    </div>
  );
}
